import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import Input from '../../shared/TailwindComponents/Input';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import apis from '../../../utils/apis';
import { PiTrashLight } from 'react-icons/pi';

interface InviteRow {
  id: string;
  email: string;
  role: string;
}

interface AdminInviteUserModalProps {
  onConfirm: () => void;
  onCancel: () => void;
}

const AdminInviteUserModal: React.FC<AdminInviteUserModalProps> = ({
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [invitees, setInvitees] = useState<InviteRow[]>([
    { id: '1', email: '', role: '' },
  ]);
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isSending, setIsSending] = useState(false);

  React.useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const response = await apis.get('/admin/roles');
      if (response.data.success) {
        const roleOptions = response.data.data.map((role: any) => ({
          value: role.name,
          label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
        }));
        setRoles(roleOptions);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleAddRow = () => {
    setInvitees([
      ...invitees,
      { id: Date.now().toString(), email: '', role: '' },
    ]);
  };

  const handleRemoveRow = (id: string) => {
    setInvitees(invitees.filter((row) => row.id !== id));
  };

  const handleInviteeChange = (id: string, field: string, value: string) => {
    setInvitees(
      invitees.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const handleSendInvites = async () => {
    const validInvitees = invitees.filter((inv) => inv.email && inv.role);

    if (validInvitees.length === 0) {
      return;
    }

    try {
      setIsSending(true);

      for (const invitee of validInvitees) {
        await apis.post('/admin/users/invite', {
          email: invitee.email,
          role_name: invitee.role,
        });
      }

      onConfirm();
    } catch (error) {
      console.error('Error sending invites:', error);
    } finally {
      setIsSending(false);
    }
  };

  const validInvitees = invitees.filter((inv) => inv.email && inv.role);
  const hasEmptyRows = invitees.some((inv) => !inv.email || !inv.role);

  return (
    <div className="min-w-[600px] sm:w-full">
      <div className="px-6 py-4 space-y-4">
        <div className="space-y-3">
          {invitees.map((invitee, index) => (
            <div key={invitee.id} className="flex gap-3 items-end">
              <Input
                label={index === 0 ? t('Email') : undefined}
                type="email"
                value={invitee.email}
                onChange={(e) =>
                  handleInviteeChange(invitee.id, 'email', e.target.value)
                }
                placeholder={t('user@example.com')}
              />

              <Dropdown
                label={index === 0 ? t('Role') : undefined}
                options={roles}
                value={invitee.role}
                onChange={(value) =>
                  handleInviteeChange(
                    invitee.id,
                    'role',
                    typeof value === 'string' ? value : ''
                  )
                }
                fullWidth
              />

              {invitees.length > 1 && (
                <button
                  onClick={() => handleRemoveRow(invitee.id)}
                  className="p-2 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded text-danger-600 dark:text-danger-400 transition-colors"
                  disabled={isSending}
                >
                  <PiTrashLight className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={handleAddRow}
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
          disabled={hasEmptyRows || isSending}
        >
          + {t('Add Another')}
        </button>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex gap-3 justify-end px-6 py-4">
        <Button
          variant="neutral"
          size="small"
          onClick={onCancel}
          disabled={isSending}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSendInvites}
          disabled={isSending || validInvitees.length === 0 || isLoadingRoles}
        >
          {isSending ? t('Sending...') : t('Send Invites')}
        </Button>
      </div>
    </div>
  );
};

export default AdminInviteUserModal;
